import { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import './Sidebar.css';
import { 
    Home, 
    LayoutGrid, 
    Calendar, 
    ShoppingCart, 
    Settings, 
    PlusCircle, 
    LogOut, 
    MessageSquare, 
    Coffee, 
    X,
    Car,
    Key,
    Fuel,
    ShieldCheck
} from 'lucide-react';
import { formatImgUrl } from '../../../utils/imageUtils';

const Sidebar = () => {
    const navigate = useNavigate();
    const [isMobileOpen, setIsMobileOpen] = useState(false);
    
    useEffect(() => {
        const handler = () => setIsMobileOpen(prev => !prev);
        window.addEventListener('togglePortalSidebar', handler);
        return () => window.removeEventListener('togglePortalSidebar', handler);
    }, []);

    const closeSidebar = () => setIsMobileOpen(false);

    const entity = JSON.parse(localStorage.getItem('entity') || '{}');
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const isCarRental = entity.entityType === 'car';

    const displayName = entity.name || user.name || (isCarRental ? 'Car Rental Partner' : 'Somstay Partner');
    const displayRole = isCarRental ? 'Fleet Manager' : (user.role || 'Partner');
    
    const profileImageUrl = entity.image || user.image;
    const displayImg = profileImageUrl ? formatImgUrl(profileImageUrl) : `https://api.dicebear.com/7.x/avataaars/svg?seed=${displayName}`;

    // Dynamic menu items based on whether partner is a Car Rental or Hotel/Property
    const menuItems = isCarRental ? [
        { icon: <Home size={20} />, label: 'Rental Dashboard', path: '/' },
        { icon: <Car size={20} />, label: 'My Vehicles Fleet', path: '/listings' },
        { icon: <Calendar size={20} />, label: 'Rental Availability', path: '/availability' },
        { icon: <Key size={20} />, label: 'Car Reservations', path: '/orders' },
        { icon: <MessageSquare size={20} />, label: 'Renter Reviews', path: '/reviews' },
    ] : [
        { icon: <Home size={20} />, label: 'Dashboard', path: '/' },
        { icon: <LayoutGrid size={20} />, label: 'My Listings', path: '/listings' },
        { icon: <Calendar size={20} />, label: 'Availability', path: '/availability' },
        { icon: <ShoppingCart size={20} />, label: 'Orders', path: '/orders' },
        { icon: <MessageSquare size={20} />, label: 'Reviews', path: '/reviews' },
    ];

    if (entity.entityType === 'hotel') {
        menuItems.push({ icon: <PlusCircle size={20} />, label: 'Verify Booking', path: '/verify' });
        menuItems.push({ icon: <Coffee size={20} />, label: 'Extra Services', path: '/services' });
    }

    const handleLogout = () => {
        localStorage.clear();
        navigate('/login');
    };

    return (
        <>
            {/* Mobile overlay backdrop */}
            {isMobileOpen && (
                <div className="sidebar-overlay" onClick={closeSidebar}></div>
            )}
            <aside className={`sidebar ${isMobileOpen ? 'mobile-open' : ''}`}>
                {/* Close button for mobile */}
                <button className="sidebar-close-btn" onClick={closeSidebar}>
                    <X size={22} />
                </button>

                <nav className="sidebar-nav">
                    {menuItems.map((item, index) => (
                        <NavLink
                            key={index}
                            to={item.path}
                            className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
                            onClick={closeSidebar}
                        >
                            {item.icon}
                            <span>{item.label}</span>
                        </NavLink>
                    ))}
                </nav>

                <div className="sidebar-footer">
                    <NavLink to="/settings" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`} onClick={closeSidebar}>
                        <Settings size={20} />
                        <span>Rental Settings</span>
                    </NavLink>
                    <div className="nav-item logout-btn" onClick={handleLogout}>
                        <LogOut size={20} />
                        <span>Logout</span>
                    </div>
                    <div className="user-profile">
                        <img src={displayImg} alt="Profile" />
                        <div className="user-info">
                            <p className="user-name">{displayName}</p>
                            <p className="user-role">{displayRole}</p>
                        </div>
                    </div>
                </div>
            </aside>
        </>
    );
};

export default Sidebar;
